import type { FileInfo } from "../../api/types/commonApiTypes";
import type { ConnectionCheckResult } from "../../auth/accessCheck/types";

export const MAX_RETRY_ATTEMPTS = 3;
export const RETRY_PAUSE_MS = 3000;

export interface RetryDeps {
  probe: (url: string) => Promise<ConnectionCheckResult>;
  waitFn: (ms: number) => Promise<void>;
  isSasExpired: (url: string) => boolean;
  refreshUrl?: () => Promise<FileInfo | undefined>;
  isCancelled: () => boolean;
  onStateChange?: (state: RetryStateSnapshot) => void;
  onAttempt?: (attempt: number, urlRefreshed: boolean) => void;
}

export interface RetryStateSnapshot {
  url: string;
  refreshed: boolean;
  sasExpired: boolean;
}

export interface RetryResult {
  outcome: "probe-ok" | "exhausted" | "cancelled";
  url: string;
  fileInfo?: FileInfo;
  refreshed: boolean;
  sasExpired: boolean;
  attempts: number;
  lastFailedProbe?: ConnectionCheckResult;
}

// Inspecting proxies (Zscaler etc.) block delivery while their content-scan verdict
// is pending; observed verdicts clear within ~3-10s, hence the timed re-probes. An
// expired SAS is refreshed at most once, without resetting the attempt budget — the
// proxy scans the object, not the URL.
export const runDocumentRetrySequence = async (initialUrl: string, deps: RetryDeps): Promise<RetryResult> => {
  let url = initialUrl;
  let fileInfo: FileInfo | undefined;
  let refreshAttempted = false;
  let refreshed = false;
  let attempts = 0;
  let lastFailedProbe: ConnectionCheckResult | undefined;
  let sasExpired = deps.isSasExpired(url);

  const notifyStateChange = (): void => {
    deps.onStateChange?.({ url, refreshed, sasExpired });
  };

  const result = (outcome: RetryResult["outcome"]): RetryResult => ({
    outcome,
    url,
    fileInfo,
    refreshed,
    sasExpired,
    attempts,
    lastFailedProbe,
  });

  const refreshOnce = async (): Promise<void> => {
    if (refreshAttempted || !deps.refreshUrl) {
      return;
    }
    refreshAttempted = true;
    const fresh = await deps.refreshUrl();
    if (fresh) {
      url = fresh.downloadUrl;
      fileInfo = fresh;
      refreshed = true;
      notifyStateChange();
    }
    // On failure keep probing the expired URL — Azure's error still classifies honestly.
  };

  notifyStateChange();

  if (sasExpired) {
    await refreshOnce();
  }

  for (let attempt = 1; attempt <= MAX_RETRY_ATTEMPTS; attempt++) {
    // Wait first — the failure just happened; an immediate probe hits the same verdict.
    await deps.waitFn(RETRY_PAUSE_MS);
    if (deps.isCancelled()) {
      return result("cancelled");
    }
    if (deps.isSasExpired(url)) {
      if (!sasExpired) {
        sasExpired = true;
        notifyStateChange();
      }
      await refreshOnce();
      if (deps.isCancelled()) {
        return result("cancelled");
      }
    }
    attempts = attempt;
    deps.onAttempt?.(attempt, refreshed);
    const probeResult = await deps.probe(url);
    if (deps.isCancelled()) {
      return result("cancelled");
    }
    if (probeResult.ok) {
      return result("probe-ok");
    }
    lastFailedProbe = probeResult;
  }

  return result("exhausted");
};
